import React, {useEffect} from 'react';
import cn from 'classnames';
import {ParentProvider} from 'contentful/components/FlatNavigationItem/FlatNavigationItem';

import useFlatNavigation from 'coreClasses/content/hooks/useFlatNavigation';
import Skeleton from 'coreComponents/Skeleton';
import {useLocation, useLockScroll, useUI} from 'coreHooks';

import {Contentful} from 'brand/app/contentful/components';
import {useHeaderCtx} from 'brandComponents/Header/HeaderCtx';

import {IFlatNavigationProps, IMainNavigationProps, INavSkeletonProps} from './IMainNavigation';
import {MainNavigationProvider} from './MainNavigationContext';

// eslint-disable-next-line no-restricted-imports
import './MainNavigation.scss';
// eslint-disable-next-line no-restricted-imports
import './MegaMenu.scss';

const NavSkeleton = ({elements = 8}: INavSkeletonProps) => (
	<div className={cn('main-nav__skeleton')}>
		{Array.from(Array(elements).keys()).map((key) =>
			<div key={`nav-skeleton-${key}`} className={cn('main-nav__skeleton-wrapper')}>
				<Skeleton />
			</div>
		)}
	</div>
);

const FlatNavigation = ({data}: IFlatNavigationProps) => {
	const flatNavigationData = useFlatNavigation(data, '>=xx-large');

	if (flatNavigationData) {
		return (
			<ParentProvider value={{
				id: flatNavigationData?.root?.sys?.id,
				type: flatNavigationData?.root?.__typename,
			}}>
				<Contentful initialData={flatNavigationData} />
			</ParentProvider>
		);
	} else {
		return null;
	}
};

const MainNavigation = ({data}: IMainNavigationProps) => {
	const location = useLocation();
	const headerCtx = useHeaderCtx(true);
	const {isNavigationOpened} = useUI('header');
	const navigationStack = useUI('navigationStack');
	const {lockScroll} = useLockScroll({lockerClassName: 'main-navigation-opened'});

	useEffect(() => {
		// Close navigation on route change
		if (isNavigationOpened) {
			headerCtx?.handleNavigationVisibility();
		} else {
			lockScroll(false);
		}
	}, [location.currentRoute]);

	const navigationsStackSize = Object.keys(navigationStack || {}).length;

	const navigation = data?.headerCollection?.items?.[0]?.navigation;

	return (
		<MainNavigationProvider>
			<nav className="main-nav">
				<div
					className={cn('main-nav__inner', {
						'has-active': navigationsStackSize > 0,
						'exit-left': navigationsStackSize > 1,
					})}
				>
					<div className="mega-menu-bg" />
					{navigation
						? <Contentful
							initialData={navigation}
						/>
						: <NavSkeleton />
					}
					<FlatNavigation data={data} />
				</div>
			</nav>
		</MainNavigationProvider>
	);
};

export default MainNavigation;
